import { StepperNavigation, Form } from '@predoc/cow';
import { Button } from '@predoc/cow';
import { useEffect, useMemo } from 'react';
import {
  REQUEST_ACTION_COMPONENTS,
  REQUEST_ACTION_ORDER,
} from './request-action-forms/constants';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  createValidationSchema,
  getDefaultValues,
} from './request-action-forms/validation';
import { usePatientRequestContext } from '@/feature/patients/context/usePatientRequestContext';
import { RequestActionFormsData } from '@/feature/patients/types';
import { RequestLoading } from './RequestLoading';
import { RequestSidebar } from './RequestSidebar';
import { useGetOrganizationInfo } from '@/feature/profiles/hooks/organizationAPI';
import { RetrievalMethod } from '@/feature/profiles/constants';

interface RequestActionsDetailsProps {
  onPrevious: () => void;
  onComplete: () => void;
  disablePrevious?: boolean;
}

export const RequestActionsDetails = ({
  onPrevious,
  onComplete,
  disablePrevious = false,
}: RequestActionsDetailsProps) => {
  const {
    requestState,
    setRequestState,
    isEditMode,
    isLoading: isLoadingPatientRequest,
  } = usePatientRequestContext();

  const { data: organizationInfo } = useGetOrganizationInfo();

  const selectedMethods = useMemo(
    () => requestState.request?.selectedRequestMethods || [],
    [requestState.request?.selectedRequestMethods],
  );

  const validationMethods = useMemo(() => {
    if (!isEditMode) return selectedMethods;
    return selectedMethods.includes(RetrievalMethod.PROVIDER_FACILITY)
      ? [RetrievalMethod.PROVIDER_FACILITY]
      : [];
  }, [isEditMode, selectedMethods]);

  const validationSchema = useMemo(
    () =>
      createValidationSchema(validationMethods, {
        allowUnknown: isEditMode,
      }),
    [isEditMode, validationMethods],
  );

  // Get initial default values
  const defaultValues = useMemo(
    () =>
      getDefaultValues(
        selectedMethods,
        requestState.request?.actionFormsData,
        organizationInfo?.displayName,
      ),
    [
      selectedMethods,
      requestState.request?.actionFormsData,
      organizationInfo?.displayName,
    ],
  );

  const form = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues,
    mode: 'onChange',
  });

  // specifically for edit mode, since the data will be loaded after the form is created
  useEffect(() => {
    if (requestState.request?.actionFormsData) {
      const newDefaultValues = getDefaultValues(
        selectedMethods,
        requestState.request.actionFormsData,
        organizationInfo?.displayName,
      );
      form.reset(newDefaultValues);
    }
  }, [
    requestState.request?.actionFormsData,
    selectedMethods,
    organizationInfo?.displayName,
    form,
  ]);

  const selectedSet = useMemo(
    () => new Set(selectedMethods),
    [selectedMethods],
  );

  const handleNext = form.handleSubmit((data) => {
    setRequestState({
      ...requestState,
      request: {
        ...requestState.request,
        actionFormsData: isEditMode
          ? ({
              ...(requestState.request?.actionFormsData || {}),
              ...(data as RequestActionFormsData),
            } as RequestActionFormsData)
          : (data as RequestActionFormsData),
      },
    });
    onComplete();
  });

  if (isLoadingPatientRequest) {
    return <RequestLoading />;
  }

  return (
    <Form {...form}>
      <div className="flex h-full gap-6">
        <div className="flex-1">
          <div className="space-y-4 py-6">
            {REQUEST_ACTION_ORDER.map((method) => {
              if (!selectedSet.has(method)) return null;
              const Component = REQUEST_ACTION_COMPONENTS[method];
              const isReadOnly =
                isEditMode && method !== RetrievalMethod.PROVIDER_FACILITY;

              if (!isReadOnly) {
                return <Component key={method} />;
              }

              return (
                <fieldset
                  key={method}
                  disabled={true}
                  aria-disabled="true"
                  className="opacity-60"
                >
                  <Component />
                </fieldset>
              );
            })}
          </div>

          <StepperNavigation>
            <Button
              variant="outline"
              onClick={onPrevious}
              disabled={disablePrevious}
              aria-label={disablePrevious ? 'Previous disabled' : 'Previous'}
            >
              Previous
            </Button>
            <Button onClick={handleNext}>Next</Button>
          </StepperNavigation>
        </div>

        <div className="flex w-1/4 flex-shrink-0 flex-col">
          <RequestSidebar />
        </div>
      </div>
    </Form>
  );
};
